import React, { useEffect, useCallback } from 'react'
import { requestCommon, jsonResponseExtractor } from 'live/lib/utils'
import i18n from 'live/services/i18n'
import WebSettingsCard from 'live/components/ui/admin/web-settings-card'
import { useWebSettingsState } from 'live/settings/useWebSettingsState'
import globalNotification from 'live/services/global-notification'
import ActionToggle from 'live/components/ui/action-toggle'

const BASE_URL = '/services/plugin-eventbased-clock/config'

type EventbasedClockSettingsType = {
    emitFullRejectedEvents?: boolean,
    persistFullRejectedEvents?: boolean
}

const EventbasedClockSettings = () => {
    const [eventbasedClockSettings, setEventbasedClockSettings] = React.useState<EventbasedClockSettingsType>({
        emitFullRejectedEvents: undefined,
        persistFullRejectedEvents: undefined

    })
    const { state, actions } = useWebSettingsState(eventbasedClockSettings)

    const fetchItems = useCallback(() => {
        api.get()
            .then(jsonResponseExtractor)
            .then(eventbasedClockSettings => {
                setEventbasedClockSettings(eventbasedClockSettings as EventbasedClockSettingsType)
                actions.setPersistedData(eventbasedClockSettings)
            })
    }, [actions])

    useEffect(fetchItems, [])

    const onSave = React.useCallback(() => {
        actions.submit()

        api.persist(eventbasedClockSettings)
            .then(jsonResponseExtractor)
            .then((eventbasedClockSettings) => {
                setEventbasedClockSettings(eventbasedClockSettings as EventbasedClockSettingsType)
                actions.setPersistedData(eventbasedClockSettings)

                actions.saveSuccess(eventbasedClockSettings)
                globalNotification.success(i18n('Settings successfully saved'))
            })
            .catch(async r => {
                actions.setError(i18n('There was an error saving the event-based clock settings'))
            }) 
    }, [actions, eventbasedClockSettings])

    const { emitFullRejectedEvents, persistFullRejectedEvents } = eventbasedClockSettings

    return (
        <WebSettingsCard
            name="export-settings"
            className="live-feature mb-15"
            open={state.open}
            error={(state.submitted && state.open) ? state.error || undefined : undefined}
            saved={state.open && state.saved}
            save={onSave}
            toggle={actions.toggle}
            onCancel={() => {
                setEventbasedClockSettings(state.persistedData)
            }}
        >
            <div className="form-group">
                <label>{i18n('Emit full rejected events')}</label>
                <div className="flex">
                    <div className="flex-1">
                        <ActionToggle
                            onChange={() => {
                                setEventbasedClockSettings({
                                    ...eventbasedClockSettings,
                                    emitFullRejectedEvents: !emitFullRejectedEvents
                                })
                            }}
                            elLeft={<span>on</span>}
                            elRight={<span>off</span>}
                            active={emitFullRejectedEvents}
                            disabled={!state.open}
                        />
                    </div>
                    <div className="flex-1">
                        <div className="flex">
                            <span className="help-block">
                                <i className="fa fa-info-circle" />
                            </span>
                            <span className="help-block">
                                <span>
                                    {i18n(
                                        'Defines if the full eventbasedclock_event_rejected are emitted. When false we will have only the aggregated.'
                                    )}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-group">
                <label>{i18n('Persist full rejected events')}</label>
                <div className="flex">
                    <div className="flex-1">
                        <ActionToggle
                            onChange={() => {
                                setEventbasedClockSettings({
                                    ...eventbasedClockSettings,
                                    persistFullRejectedEvents: !persistFullRejectedEvents
                                })
                            }}
                            elLeft={<span>on</span>}
                            elRight={<span>off</span>}
                            active={persistFullRejectedEvents}
                            disabled={!state.open}
                        />
                    </div>
                    <div className="flex-1">
                        <div className="flex">
                            <span className="help-block">
                                <i className="fa fa-info-circle" />
                            </span>
                            <span className="help-block">
                                <span>
                                    {i18n(
                                        'If false the events are created with __skipstorage:true (i.e. only available in realtime but not saved in Live storage)'
                                    )}
                                </span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </WebSettingsCard>
    )
}

const api = {
    get() {
        return fetch(BASE_URL, {
            credentials: 'same-origin'
        })
    },
    persist(settings: EventbasedClockSettingsType) {
        return fetch(BASE_URL, {
            method: 'POST',
            ...requestCommon,
            body: JSON.stringify(settings)
        })
    }
}

export default {
    persist: api.persist,
    configComponent: EventbasedClockSettings,
    section: i18n('Event-based clock settings'),
    name: i18n('Event-based clock')
}
